import { CalendarClock, CalendarDays, MapPin } from "lucide-react";
import { CardShell, LiveBadge, ScheduledBadge, parsePaths, type MetaFact } from "./CardShell";
import { isScheduled, scheduledDateLabel } from "../scheduled";
import { type Dict, type Lang } from "@/lib/i18n";
import { modulePoster } from "@/lib/lecturehub/poster";
import { moduleHref } from "../moduleHref";
import { formatDate } from "@/lib/format";
import type { Module } from "@/lib/lecturehub/types";
import { isFreeToUnlock } from "../paywall";

export function EventCard({
  lang,
  t,
  module: m,
  enrolled = false,
  cfHash = null,
  library = false,
}: {
  lang: Lang;
  t: Dict;
  module: Module;
  enrolled?: boolean;
  cfHash?: string | null;
  library?: boolean;
}) {
  const ev = m.event;
  const live = ev?.eventType === "live";
  const location =
    ev?.city && ev?.country ? `${ev.city}, ${ev.country}` : (ev?.city ?? ev?.country ?? null);
  const paths = parsePaths(m.keywords, lang);
  const href = moduleHref(lang, m, library);

  const scheduled = isScheduled(m);
  const scheduledLabel = scheduledDateLabel(t.detail, m.publishAt, lang);

  const facts: MetaFact[] = [];
  if (scheduledLabel) {
    facts.push({ Icon: CalendarClock, label: scheduledLabel });
  }
  if (ev?.dateFrom) {
    facts.push({ Icon: CalendarDays, label: formatDate(ev.dateFrom, lang) });
  }
  if (location) {
    facts.push({ Icon: MapPin, label: location });
  }

  // Scheduled (unpublished) wins the overlay — there's nothing live to attend
  // yet. Otherwise only a genuinely-urgent LIVE event gets a poster overlay.
  // Past events are NOT archived/faded — the date is just when the event was
  // organized, not a publish gate (all published events show as regular content).
  const posterBadge = scheduled ? <ScheduledBadge t={t} /> : live ? <LiveBadge t={t} /> : null;

  return (
    <CardShell
      t={t}
      href={href}
      openHref={href}
      poster={modulePoster(m, cfHash)}
      posterFallback={t.detail.typeEvent}
      typeLabel={t.detail.typeEvent}
      TypeIcon={CalendarDays}
      statusLabel={!scheduled && isFreeToUnlock(m) && !live ? t.detail.free : undefined}
      posterBadge={posterBadge}
      title={m.name ?? "—"}
      abstract={m.abstract}
      enrolled={enrolled}
      paths={paths}
      facts={facts}
    />
  );
}
